/**
 * Parse-time hydration of React Query caches from localStorage.
 *
 * Runs in prefetch.ts before React mounts and before the dashboard entry
 * fires, so the first render already has data: the org's flags, pipelines and
 * object types (long-lived schema), and — when the last visit left one — the
 * dashboard's above-the-fold snapshot (persistedDashboardFold). Everything is
 * written STALE (`updatedAt: 0`), so the prefetch's `fetchQuery` registrations
 * still revalidate against the network; hydration decides what the first
 * frame shows, not what the truth is.
 */

import type { QueryClient, QueryKey } from '@tanstack/react-query';

import { getStoreGeneration, upsertListResponseToStore } from '@/lib/crm/store';
import { hiddenMeetingsQueryKey } from '@/lib/dashboard/hiddenMeetings';
import {
  todayMeetingsQueryOptions,
  todayRemindersQueryOptions,
  todayTasksQueryOptions,
} from '@/lib/dashboard/queries';
import { buildTodayMeetingsFilters } from '@/lib/dates/dashboardDateRange';
import {
  clearAllPersistedDashboardFold,
  getPersistedDashboardFold,
} from '@/lib/localStorage/persistedDashboardFold';
import { getPersistedDashboardPrefetch } from '@/lib/localStorage/persistedDashboardPrefetch';
import { getPersistedFeatureFlags } from '@/lib/localStorage/persistedFeatureFlags';
import { getPersistedObjectTypes } from '@/lib/localStorage/persistedObjectTypes';
import { getPersistedPipelines } from '@/lib/localStorage/persistedPipelines';
import { getSinglePersistedUserProfile } from '@/lib/localStorage/persistedUserProfile';
// Direct module import (not the `@/lib/time` barrel) — see the comment on the
// equivalent import in dashboardDateRange.ts: the barrel re-exports
// useOrgTimeFormat, which imports OrgContext.tsx, and this module is reached
// from OrgContext.tsx (via allauth.js -> clearPersistedDashboardFold), so
// importing the barrel here closes an import cycle back into OrgContext.tsx.
import { todayIn } from '@/lib/time/calendarDay';

import { prefetchDebug } from './debug';
import {
  dashboardProposalsQueryKey,
  importStatusQueryKey,
  reminderAutomationQueryKey,
  toImportStatusFetchResult,
} from './entries/dashboard';
import { seedIntegrationsListCache } from './entries/dashboardSecondarySeeds';

/** Write `data` under `key` marked stale, so consumers render it and refetch. */
function seedStale(
  queryClient: QueryClient,
  key: QueryKey,
  data: unknown
): void {
  queryClient.setQueryData(key, data, { updatedAt: 0 });
  void queryClient.invalidateQueries({ queryKey: key, refetchType: 'none' });
}

/**
 * Replay the last visit's fold snapshot into the exact keys the dashboard
 * hooks read. Guarded three ways so a shared device or a stale snapshot never
 * paints another user's (or another day's) data:
 *   - exactly one persisted profile exists (the single-user-device heuristic
 *     every pre-React read here relies on);
 *   - the snapshot's user entity matches the prefetch metadata's;
 *   - day-scoped lists replay only when the snapshot's day is still today in
 *     the profile's zone. Day-independent parts replay regardless.
 */
function hydrateDashboardFold(queryClient: QueryClient, orgId: number): void {
  const fold = getPersistedDashboardFold(orgId);
  if (fold === undefined) {
    return;
  }
  const profile = getSinglePersistedUserProfile();
  const prefetchMeta = getPersistedDashboardPrefetch(orgId);
  if (profile === undefined || prefetchMeta === undefined) {
    prefetchDebug(
      'hydrateDashboardFold: rejected (no single persisted profile)',
      {
        orgId,
      }
    );
    return;
  }
  if (prefetchMeta.userEntityId !== fold.userEntityId) {
    prefetchDebug(
      'hydrateDashboardFold: rejected (profile-mismatch — different user entity)',
      {
        orgId,
        snapshotUserEntityId: fold.userEntityId,
        currentUserEntityId: prefetchMeta.userEntityId,
      }
    );
    return;
  }

  if (fold.proposals) {
    seedStale(queryClient, dashboardProposalsQueryKey(orgId), fold.proposals);
  }
  if (fold.onboardingStatus) {
    seedStale(queryClient, ['onboarding-status', orgId], fold.onboardingStatus);
  }
  if (fold.importJobs) {
    seedStale(
      queryClient,
      importStatusQueryKey(orgId),
      toImportStatusFetchResult(fold.importJobs)
    );
  }
  if (fold.reminderAutomationEnabled !== null) {
    seedStale(
      queryClient,
      reminderAutomationQueryKey(orgId),
      fold.reminderAutomationEnabled
    );
  }
  // The hidden set is server-confirmed from the last visit; the live prefetch
  // overwrites it unconditionally when it lands. Seeding it is what lets the
  // meetings hook enable itself on the first render.
  queryClient.setQueryData(hiddenMeetingsQueryKey(orgId), fold.hiddenMeetings);
  if (fold.userEntityId !== null) {
    seedIntegrationsListCache(
      queryClient,
      orgId,
      fold.userEntityId,
      fold.integrationEntityIds
    );
  }

  const zone = profile.user.timezone || fold.timezone;
  const sameDay = zone === fold.timezone && todayIn(zone) === fold.dayKey;
  if (!sameDay) {
    prefetchDebug(
      'hydrateDashboardFold: day-scoped lists rejected (same-day guard: snapshot day does not match today)',
      {
        orgId,
        snapshotDayKey: fold.dayKey,
        todayIn: todayIn(zone),
      }
    );
    return;
  }

  const generation = getStoreGeneration();
  if (fold.meetings) {
    const meetingsQuery = todayMeetingsQueryOptions(
      orgId,
      buildTodayMeetingsFilters(
        fold.rangeStartISO,
        fold.rangeEndISO,
        fold.integrationEntityIds,
        fold.userEntityId,
        fold.hiddenMeetings.seriesIds,
        fold.hiddenMeetings.meetingEntityIds
      )
    );
    seedStale(
      queryClient,
      meetingsQuery.queryKey,
      upsertListResponseToStore(fold.meetings, generation)
    );
  }
  if (fold.reminders) {
    seedStale(
      queryClient,
      todayRemindersQueryOptions(
        orgId,
        fold.reminderStartDate,
        fold.reminderEndDate,
        fold.userEntityId
      ).queryKey,
      upsertListResponseToStore(fold.reminders, generation)
    );
  }
  if (fold.tasks) {
    seedStale(
      queryClient,
      todayTasksQueryOptions(
        orgId,
        fold.reminderStartDate,
        fold.reminderEndDate,
        fold.rangeStartISO,
        fold.rangeEndISO
      ).queryKey,
      upsertListResponseToStore(fold.tasks, generation)
    );
  }
  prefetchDebug('hydrateDashboardFold: seeded', { orgId, dayKey: fold.dayKey });
}

/**
 * Remove the persisted dashboard fold for all orgs. Call synchronously on
 * logout — before any redirect or unmount — so a fast logout→login-as-
 * different-user sequence can never paint the prior user's data.
 */
export function clearPersistedDashboardFold(): void {
  clearAllPersistedDashboardFold();
}

export function hydratePersistedQueries(
  queryClient: QueryClient,
  orgId: number
): void {
  try {
    const flags = getPersistedFeatureFlags(orgId);
    if (flags !== undefined) {
      seedStale(queryClient, ['featureFlags', orgId], flags);
      prefetchDebug('hydratePersistedQueries: featureFlags seeded', { orgId });
    }
  } catch (err) {
    prefetchDebug('hydratePersistedQueries: featureFlags error', {
      error: String(err),
    });
  }

  try {
    const pipelines = getPersistedPipelines(orgId);
    if (pipelines !== undefined) {
      seedStale(queryClient, ['pipelines', orgId, undefined], pipelines);
      prefetchDebug('hydratePersistedQueries: pipelines seeded', { orgId });
    }
  } catch (err) {
    prefetchDebug('hydratePersistedQueries: pipelines error', {
      error: String(err),
    });
  }

  try {
    const objectTypes = getPersistedObjectTypes(orgId);
    if (objectTypes !== undefined) {
      seedStale(
        queryClient,
        ['objectTypes', orgId, 'navCustomTypes'],
        objectTypes
      );
      prefetchDebug('hydratePersistedQueries: objectTypes seeded', { orgId });
    }
  } catch (err) {
    prefetchDebug('hydratePersistedQueries: objectTypes error', {
      error: String(err),
    });
  }

  try {
    hydrateDashboardFold(queryClient, orgId);
  } catch (err) {
    prefetchDebug('hydratePersistedQueries: dashboard fold error', {
      error: String(err),
    });
  }
}
